import {
  Component,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { FeatureFlagsEnum } from 'src/app/feature-flag-constant';
import { BackendConfig } from 'src/app/models/build-info.model';
import { PrivilegedRole, UserRole } from 'src/app/models/user.model';
import { BuildInfoService } from 'src/app/services/build-info.service';
import { FeatureFlagService } from 'src/app/services/feature-flag.service';
import { buildInfo } from '../../../build-info';
import { selectProfileState } from '../../stores/profile/profile.selector';
import { ProfileState } from '../../stores/store.states';

interface MenuItem {
  id: string;
  label: string;
  link?: string;
  icon: string;
  alt: string;
  roles: string[] | null;
  hasFeature$: Observable<boolean>;
  isActive: (url: string) => boolean;
  children?: MenuItem[];
}

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  standalone: false,
})
export class SidenavComponent implements OnInit, OnDestroy {
  url: string = '';
  clickOn: boolean = false;
  @Output() logoutEvent = new EventEmitter();
  profile$: Observable<ProfileState> | undefined;
  expandedMenuState: Record<string, boolean> = {};

  private featureFlagService = inject(FeatureFlagService);

  readonly frontendBuild = buildInfo;
  /** Cold until the template subscribes, so only masteradmins trigger the request. */
  readonly backendConfig$: Observable<BackendConfig | null> =
    inject(BuildInfoService).getBackendConfig();

  menuItems: MenuItem[] = [
    {
      id: 'resources',
      label: 'Resources',
      link: '/resources',
      icon: '../assets/svg/equipment.svg',
      alt: 'resources - Quantum Systems',
      roles: null,
      hasFeature$: this.featureFlagService.isFeatureEnabled(
        FeatureFlagsEnum.RESOURCES_PAGE,
      ),
      isActive: (url: string) =>
        url.startsWith('/resources') ||
        url.startsWith('/device') ||
        url.startsWith('/drones'),
    },
    {
      id: 'dashboard',
      label: 'Dashboard',
      link: '/dashboard',
      icon: '../assets/svg/dashboard.svg',
      alt: 'dashboard - Quantum Systems',
      // Every dashboard tile is a report query, and those are administrator/maintainer only.
      roles: ['ADMIN', 'MAINTAINER'],
      hasFeature$: of(true),
      isActive: (url: string) => url.startsWith('/dashboard'),
    },
    {
      id: 'uploads',
      label: 'Uploads',
      link: '/uploads',
      icon: '../assets/svg/uploads.svg',
      alt: 'uploads - Quantum Systems',
      roles: ['ADMIN', 'MAINTAINER'],
      hasFeature$: this.featureFlagService.isFeatureEnabled(
        FeatureFlagsEnum.UPLOAD_PAGE,
      ),
      isActive: (url: string) => url.startsWith('/uploads'),
    },
  ];
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private store: Store,
  ) {
    this.url = this.router.url;
    this.router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        this.url = val.url;
      }
    });
  }

  ngOnInit(): void {
    this.profile$ = this.store.select(selectProfileState);
    this.addAdministrativeMenuItems();
  }

  childMethod() {
    this.logoutEvent.emit();
  }

  opening() {
    this.clickOn = !this.clickOn;
  }

  isMenuExpanded(menu: MenuItem): boolean {
    if (!menu.children?.length) {
      return false;
    }
    const storedState = this.expandedMenuState[menu.id];
    if (storedState !== undefined) {
      return storedState;
    }
    return menu.isActive(this.url);
  }

  toggleMenu(menu: MenuItem): void {
    if (!menu.children?.length) {
      return;
    }
    this.expandedMenuState[menu.id] = !this.isMenuExpanded(menu);
  }

  onMenuGroupKeydown(event: KeyboardEvent, menu: MenuItem): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleMenu(menu);
    }
  }

  /** The administrative sections a production engineer needs to register and assign devices. */
  private static readonly PRODUCTION_MENU_ITEM_IDS = [
    'device_registration',
    'all_resources',
    'companies',
  ];

  /**
   * The administrative sections a support user may read. Everything they may look at but nothing
   * they could only act on: registering devices is not theirs.
   */
  private static readonly SUPPORT_MENU_ITEM_IDS = [
    'all_resources',
    'companies',
    'license_products',
    'component_credentials',
    'license_batches',
  ];

  addAdministrativeMenuItems(): void {
    this.profile$
      ?.pipe(
        filter(
          (profile) =>
            profile.isMasteradmin ||
            profile.privilegedRole === PrivilegedRole.PRODUCTION ||
            profile.privilegedRole === PrivilegedRole.SUPPORT ||
            profile.role === UserRole.ADMIN,
        ),
        take(1),
        takeUntil(this.destroy$),
      )
      .subscribe((profile) => {
        const isProductionEngineer =
          profile.privilegedRole === PrivilegedRole.PRODUCTION;
        const isSupportUser = profile.privilegedRole === PrivilegedRole.SUPPORT;

        if (!profile.isMasteradmin && !isProductionEngineer && !isSupportUser) {
          if (!this.menuItems.some((x) => x.id === 'license_batches')) {
            this.menuItems.push({
              id: 'license_batches',
              label: 'License Batches',
              link: '/licenses/batches',
              icon: '../assets/svg/equipment.svg',
              alt: 'License Batches - Quantum Systems',
              roles: null,
              hasFeature$: this.featureFlagService.isFeatureEnabled(
                FeatureFlagsEnum.LICENSE_MANAGEMENT,
              ),
              isActive: (url: string) => url.startsWith('/licenses/batches'),
            });
          }
          return;
        }

        if (!this.menuItems.some((x) => x.id === 'administrative')) {
          this.menuItems.push({
            id: 'administrative',
            label: 'Administrative',
            icon: '../assets/svg/admin.svg',
            alt: 'Administrative - Quantum Systems',
            roles: null,
            hasFeature$: of(true),
            isActive: (url: string) =>
              url.startsWith('/admin/register-aircraft') ||
              url.startsWith('/admin/device-registration') ||
              url.startsWith('/admin/all-resources') ||
              url.startsWith('/admin/companies') ||
              url.startsWith('/admin/licenses/products') ||
              url.startsWith('/admin/licenses/component-credentials') ||
              url.startsWith('/admin/licenses/batches'),
            children: [
              {
                id: 'register_uav',
                label: 'Add UAVs',
                link: '/admin/register-aircraft',
                icon: '../assets/svg/uploads.svg',
                alt: 'Add UAVs - Quantum Systems',
                roles: null,
                hasFeature$: this.featureFlagService.isFeatureEnabled(
                  FeatureFlagsEnum.ADMIN_PAGE,
                ),
                isActive: (url: string) =>
                  url.startsWith('/admin/register-aircraft'),
              },
              {
                id: 'device_registration',
                label: 'Device Registration',
                link: '/admin/device-registration',
                icon: '../assets/svg/app_reg.svg',
                alt: 'Device Registration - Quantum Systems',
                roles: null,
                hasFeature$: this.featureFlagService.isFeatureEnabled(
                  FeatureFlagsEnum.CERTIFICATE,
                ),
                isActive: (url: string) =>
                  url.startsWith('/admin/device-registration'),
              },
              {
                id: 'all_resources',
                label: 'All Resources',
                link: '/admin/all-resources',
                icon: '../assets/svg/equipment.svg',
                alt: 'All Resources - Quantum Systems',
                roles: null,
                hasFeature$: this.featureFlagService.isFeatureEnabled(
                  FeatureFlagsEnum.ADMIN_PAGE,
                ),
                isActive: (url: string) =>
                  url.startsWith('/admin/all-resources'),
              },
              {
                id: 'companies',
                label: 'Companies',
                link: '/admin/companies',
                icon: '../assets/svg/business.svg',
                alt: 'Companies - Quantum Systems',
                roles: null,
                hasFeature$: this.featureFlagService.isFeatureEnabled(
                  FeatureFlagsEnum.ADMIN_PAGE,
                ),
                isActive: (url: string) => url.startsWith('/admin/companies'),
              },
              {
                id: 'license_products',
                label: 'Licensed Products',
                link: '/admin/licenses/products',
                icon: '../assets/svg/product.svg',
                alt: 'Licensed Products - Quantum Systems',
                roles: null,
                hasFeature$: this.featureFlagService.isFeatureEnabled(
                  FeatureFlagsEnum.LICENSE_MANAGEMENT,
                ),
                isActive: (url: string) =>
                  url.startsWith('/admin/licenses/products'),
              },
              {
                id: 'component_credentials',
                label: 'Component Credentials',
                link: '/admin/licenses/component-credentials',
                icon: '../assets/svg/password.svg',
                alt: 'Component Credentials - Quantum Systems',
                roles: null,
                hasFeature$: this.featureFlagService.isFeatureEnabled(
                  FeatureFlagsEnum.LICENSE_MANAGEMENT,
                ),
                isActive: (url: string) =>
                  url.startsWith('/admin/licenses/component-credentials'),
              },
              {
                id: 'license_batches',
                label: 'License Batches',
                link: '/admin/licenses/batches',
                icon: '../assets/svg/license.svg',
                alt: 'License Batches - Quantum Systems',
                roles: null,
                hasFeature$: this.featureFlagService.isFeatureEnabled(
                  FeatureFlagsEnum.LICENSE_MANAGEMENT,
                ),
                isActive: (url: string) =>
                  url.startsWith('/admin/licenses/batches'),
              },
            ].filter(
              // A masteradmin sees everything; a production engineer only the sections they work
              // in, and a support user only the ones they may read.
              (child) =>
                profile.isMasteradmin ||
                (isProductionEngineer &&
                  SidenavComponent.PRODUCTION_MENU_ITEM_IDS.includes(
                    child.id,
                  )) ||
                (isSupportUser &&
                  SidenavComponent.SUPPORT_MENU_ITEM_IDS.includes(child.id)),
            ),
          });
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
